import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { supabase } from '../supabase';
import './ProductDetail.css';

function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { currency } = useCurrency();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [isMobile, setIsMobile] = useState(false);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Helper function to get all media - moved here to fix hoisting issue
  const getAllMedia = () => {
    if (!product) return [];
    
    const allImages = product.image_url ? [product.image_url, ...(product.gallery_images || [])] : (product.gallery_images || []);
    const allMedia = [...allImages];
    if (product.video_url) {
      // Generate thumbnail for video if not already generated
      if (!videoThumbnails[product.video_url]) {
        generateVideoThumbnail(product.video_url, product.video_url);
      }
      
      allMedia.push({ 
        type: 'video', 
        url: product.video_url, 
        thumbnail: product.video_thumbnail || videoThumbnails[product.video_url] || null
      });
    }
    return allMedia;
  };

  // Helper function to generate video thumbnails - moved here to fix hoisting issue
  // Helper function to generate video thumbnails - moved to top of component

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    checkMobile();
    window.addEventListener('resize', checkMobile);
    // Scroll to top when product changes
    window.scrollTo(0, 0);
    return () => window.removeEventListener('resize', checkMobile);
  }, [productId]);

  // Keyboard navigation for images
  useEffect(() => {
    if (!product) return;

    const handleKeyDown = (e) => {
      const allMedia = getAllMedia();
      if (allMedia.length > 1) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          handlePreviousImage();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          handleNextImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [product]);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  useEffect(() => {
    fetchProduct();
    // Scroll to top when component mounts or currency changes
    window.scrollTo(0, 0);
  }, [productId, currency]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();

      if (error) {
        console.error('Error fetching product:', error);
        setProduct(null);
      } else {
        setProduct(data);
      }
    } catch (error) {
      console.error('Error:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      setReviewsLoading(true);
      console.log('Fetching reviews for product:', productId);
      
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
        setReviews([]);
      } else {
        console.log('Reviews found:', data);
        setReviews(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="product-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  // Function to check if product is available in selected currency and region
  const isProductAvailableInCurrency = () => {
    if (!product) return false;
    
    // Get target country based on currency
    let targetCountry = '';
    if (currency === '$') targetCountry = 'USA';
    else if (currency === '£') targetCountry = 'UK';
    else if (currency === 'AED') targetCountry = 'UAE';
    else if (currency === 'KSA') targetCountry = 'Saudi Arabia';
    
    // Check if product is available in the target country
    const countries = product.countries || [];
    const isAvailableInCountry = countries.includes(targetCountry);
    
    // Check if product has price in selected currency
    let hasPriceInCurrency = false;
    if (currency === '$') {
      hasPriceInCurrency = product.price_usd && product.price_usd > 0;
    } else if (currency === '£') {
      hasPriceInCurrency = product.price_gbp && product.price_gbp > 0;
    } else if (currency === 'AED') {
      hasPriceInCurrency = product.price_aed && product.price_aed > 0;
    } else if (currency === 'KSA') {
      hasPriceInCurrency = product.price_sar && product.price_sar > 0;
    }
    
    // Product must be available in both country AND have price in currency
    return isAvailableInCountry && hasPriceInCurrency;
  };

  // Function to get region name for currency
  const getRegionName = () => {
    if (currency === '$') return 'United States';
    else if (currency === '£') return 'United Kingdom';
    else if (currency === 'AED') return 'United Arab Emirates';
    else if (currency === 'KSA') return 'Saudi Arabia';
    return 'this region';
  };

  if (!product) {
    return (
      <div className="product-not-found">
        <div className="not-found-content">
          <div className="not-found-icon">🔍</div>
        <h2>Product Not Found</h2>
          <p>The product you're looking for doesn't exist or has been removed.</p>
        <button onClick={() => navigate('/')} className="back-button">
            ← Back to Home
        </button>
        </div>
      </div>
    );
  }

  // Check if product is available in selected currency and region
  if (!isProductAvailableInCurrency()) {
    return (
      <div className="product-not-available">
        <div className="not-available-content">
          <div className="not-available-icon">🌍</div>
          <h2>Product Not Available in Your Region</h2>
          <p>This product is not available in {getRegionName()}. Please choose some other product in your region.</p>
          <button onClick={() => navigate('/products')} className="back-button">
            ← Back to Products
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    // Check if variations are required but not selected
    if (product.has_variations && product.variationTypes && product.variationTypes.length > 0 && !selectedVariation) {
      // Instead of alert, we'll show a visual indicator in the UI
      // The button will be disabled and show a message
      return;
    }

    // Determine the correct price based on selected currency
    let finalPrice = parseFloat(getPriceForCurrency(product, 'current')) || 0;
    if (selectedVariation && selectedVariation.price) {
      const variationPrice = parseFloat(selectedVariation.price);
      if (!isNaN(variationPrice) && variationPrice > 0) {
        finalPrice = variationPrice;
      }
    }

    // Determine the correct image
    let finalImage = product.image_url || '';
    if (selectedVariation && selectedVariation.image_url) {
      finalImage = selectedVariation.image_url;
    }

    // Create product name with variation
    let finalName = product.name;
    if (selectedVariation && selectedVariation.combination) {
      finalName = `${product.name} - ${selectedVariation.combination}`;
    }

    addToCart({
      id: product.id,
      name: finalName,
      price: finalPrice,
      image: finalImage,
      quantity: quantity,
      vendor_id: product.vendor_id,
      variation: selectedVariation ? {
        combination: selectedVariation.combination,
        selectedOptions: selectedVariations
      } : null
    });
  };

  const handleImageClick = (index) => {
    setSelectedImage(index);
  };

  // Helper function to get all media - moved to top of component

  const handlePreviousImage = () => {
    const allMedia = getAllMedia();
    if (allMedia.length <= 1) return;
    setSelectedImage(prev => prev === 0 ? allMedia.length - 1 : prev - 1);
  };

  const handleNextImage = () => {
    const allMedia = getAllMedia();
    if (allMedia.length <= 1) return;
    setSelectedImage(prev => prev === allMedia.length - 1 ? 0 : prev + 1);
  };



  const getDiscountPercentage = () => {
    if (product.original_price && product.price) {
      const discount = ((product.original_price - product.price) / product.original_price) * 100;
      return Math.round(discount);
    }
    return 0;
  };

  // Function to get the correct price based on selected currency
  const getPriceForCurrency = (product, priceType = 'current') => {
    if (currency === '$') {
      return priceType === 'current' ? (product.price_usd || 0) : (product.original_price_usd || 0);
    } else if (currency === '£') {
      return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
    } else if (currency === 'AED') {
      return priceType === 'current' ? (product.price_aed || 0) : (product.original_price_aed || 0);
    } else if (currency === 'KSA') {
      return priceType === 'current' ? (product.price_sar || 0) : (product.original_price_sar || 0);
    }
    // Default fallback to GBP
    return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
  };

  // Function to get currency symbol
  const getCurrencySymbol = () => {
    if (currency === '$') return '$';
    else if (currency === '£') return '£';
    else if (currency === 'AED') return 'د.إ';
    else if (currency === 'KSA') return 'ر.س';
    return '£'; // Default fallback
  };

  const generateVideoThumbnail = (videoUrl, videoId) => {
    if (videoThumbnails[videoId]) return videoThumbnails[videoId];
    
    // Create a video element to capture a frame
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      // Seek to 2 seconds to get a good frame
      video.currentTime = 2;
    };
    
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 240;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
        setVideoThumbnails(prev => ({
          ...prev,
          [videoId]: thumbnailUrl
        }));
      } catch (error) {
        console.log('CORS prevented thumbnail generation, using video element');
        // If CORS blocks us, we'll use the video element directly
        setVideoThumbnails(prev => ({
          ...prev,
          [videoId]: 'video-element'
        }));
      }
    };
    
    video.onerror = () => {
      console.log('Video failed to load, using placeholder');
      setVideoThumbnails(prev => ({
        ...prev,
        [videoId]: 'placeholder'
      }));
    };
    
    video.src = videoUrl;
    video.load();
  };

  // Get all media for rendering
  const allMedia = getAllMedia();

  return (
    <div className="product-detail">
      <div className="container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span onClick={() => navigate('/')} className="breadcrumb-link">Home</span>
          <span className="breadcrumb-separator">›</span>
          <span onClick={() => navigate('/products')} className="breadcrumb-link">Products</span>
          <span className="breadcrumb-separator">›</span>
          {product.category && (
            <>
              <span onClick={() => navigate(`/products?category=${encodeURIComponent(product.category)}`)} className="breadcrumb-link">{product.category}</span>
              <span className="breadcrumb-separator">›</span>
            </>
          )}
          {(() => {
            try {
              const subcategories = product.subcategory ? 
                (typeof product.subcategory === 'string' ? JSON.parse(product.subcategory) : product.subcategory) : 
                [];
              return subcategories.length > 0 ? (
                <>
                  {subcategories.map((subcategory, index) => (
                    <React.Fragment key={index}>
                      <span 
                        onClick={() => navigate(`/products?category=${encodeURIComponent(product.category)}&subcategory=${encodeURIComponent(subcategory)}`)} 
                        className="breadcrumb-subcategory"
                      >
                        {subcategory}
                      </span>
                      {index < subcategories.length - 1 && <span className="breadcrumb-separator">›</span>}
                    </React.Fragment>
                  ))}
                  <span className="breadcrumb-separator">›</span>
                </>
              ) : null;
            } catch (e) {
              console.warn('Error parsing subcategory JSON:', e);
              return null;
            }
          })()}
          <span className="breadcrumb-current">{product.name}</span>
        </div>

        <div className="product-detail-content">
          {/* Product Images */}
          <div className="product-images-section">
            <div className="main-image-container">
              <div className="main-image">
                {allMedia.length > 0 ? (
                  allMedia[selectedImage] && typeof allMedia[selectedImage] === 'object' && allMedia[selectedImage].type === 'video' ? (
                    <video 
                      src={allMedia[selectedImage].url} 
                      controls
                      poster={videoThumbnails[allMedia[selectedImage].url] && videoThumbnails[allMedia[selectedImage].url].startsWith('data:') ? videoThumbnails[allMedia[selectedImage].url] : ''}
                      className="product-video"
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img 
                      src={allMedia[selectedImage]} 
                      alt={product.name}
                      loading="lazy"
                    />
                  )
                ) : (
                  <div className="no-image-placeholder">
                    <span>📦</span>
                    <p>No image available</p>
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              {allMedia.length > 1 && (
                <>
                  <button 
                    className="image-nav-btn prev-btn" 
                    onClick={handlePreviousImage}
                    aria-label="Previous image"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6"/>
                    </svg>
                  </button>
                  <button 
                    className="image-nav-btn next-btn" 
                    onClick={handleNextImage}
                    aria-label="Next image"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </button>
                </>
              )}
              
              {/* Product Badges */}
              <div className="product-badges-container">
                {product.is_featured && <div className="badge featured">Featured</div>}
                {product.is_bestseller && <div className="badge bestseller">Best Seller</div>}
                {product.is_new_arrival && <div className="badge new">New</div>}
                {getDiscountPercentage() > 0 && (
                  <div className="badge discount">-{getDiscountPercentage()}%</div>
                )}
              </div>
            </div>
            
            {allMedia.length > 1 && (
            <div className="thumbnail-images">
                {allMedia.map((media, index) => (
                <div 
                  key={index} 
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => handleImageClick(index)}
                >
                  {media && typeof media === 'object' && media.type === 'video' ? (
                    <div className="video-thumbnail">
                      {videoThumbnails[media.url] && videoThumbnails[media.url].startsWith('data:') ? (
                        // Show generated thumbnail image
                        <img 
                          src={videoThumbnails[media.url]} 
                          alt={`${product.name} video ${index + 1}`} 
                          className="video-thumbnail-img"
                        />
                      ) : videoThumbnails[media.url] === 'video-element' ? (
                        // Show video element as thumbnail
                        <video 
                          src={media.url}
                          muted
                          preload="metadata"
                          className="video-thumbnail-video"
                          onLoadedData={(e) => {
                            // Try to seek to a good frame
                            e.target.currentTime = 2;
                          }}
                        />
                      ) : (
                        // Show placeholder
                        <div className="video-thumbnail-content">
                          <div className="video-thumbnail-icon">🎥</div>
                          <div className="video-thumbnail-text">Video</div>
                        </div>
                      )}
                      <div className="video-play-icon">▶️</div>
                    </div>
                  ) : (
                    <img src={media} alt={`${product.name} ${index + 1}`} />
                  )}
                </div>
              ))}
            </div>
            )}
          </div>

          {/* Product Info */}
          <div className="product-info-section">
            <div className="product-header">
              <h1 className="product-title">{product.name}</h1>
              {product.subcategories && product.subcategories.length > 0 && (
                <div className="product-subcategories">
                  {product.subcategories.map((subcategory, index) => (
                    <span key={index} className="subcategory-tag">
                      {subcategory}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Category and Subcategories Info */}
            {(product.category || (product.subcategories && product.subcategories.length > 0)) && (
              <div className="product-categories-info">
                {product.category && (
                  <div className="category-item">
                    <span className="category-label">Category:</span>
                    <span className="category-value">{product.category}</span>
                  </div>
                )}
                {product.subcategories && product.subcategories.length > 0 && (
                  <div className="subcategories-item">
                    <span className="subcategories-label">Subcategories:</span>
                    <div className="subcategories-list">
                      {product.subcategories.map((subcategory, index) => (
                        <span key={index} className="subcategory-chip">{subcategory}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="product-pricing">
              <div className="price-container">
                <span className="current-price">
                  {getCurrencySymbol()}{selectedVariation && selectedVariation.price 
                    ? parseFloat(selectedVariation.price).toFixed(2) 
                    : parseFloat(getPriceForCurrency(product, 'current')).toFixed(2)
                  }
                </span>
                {selectedVariation && selectedVariation.price && getPriceForCurrency(product, 'original') > 0 && (
                  <span className="original-price">{getCurrencySymbol()}{parseFloat(getPriceForCurrency(product, 'original')).toFixed(2)}</span>
                )}
                {!selectedVariation && getPriceForCurrency(product, 'original') > 0 && (
                  <span className="original-price">{getCurrencySymbol()}{parseFloat(getPriceForCurrency(product, 'original')).toFixed(2)}</span>
                )}
                {selectedVariation && selectedVariation.price && product.original_price && (
                  <span className="discount-badge">
                    -{Math.round(((product.original_price - selectedVariation.price) / product.original_price) * 100)}%
                  </span>
                )}
                {!selectedVariation && getDiscountPercentage() > 0 && (
                  <span className="discount-badge">-{getDiscountPercentage()}%</span>
                )}
              </div>
              
              <div className="stock-status">
                {selectedVariation ? (
                  selectedVariation.stock > 0 ? (
                    <span className="in-stock">
                      <span className="status-icon">✓</span>
                      In Stock ({selectedVariation.stock} available)
                    </span>
                  ) : (
                    <span className="out-of-stock">
                      <span className="status-icon">✗</span>
                      Out of Stock
                    </span>
                  )
                ) : (
                  product.stock > 0 ? (
                    <span className="in-stock">
                      <span className="status-icon">✓</span>
                      In Stock ({product.stock} available)
                    </span>
                ) : (
                    <span className="out-of-stock">
                      <span className="status-icon">✗</span>
                      Out of Stock
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Product Variations */}
            {product.has_variations && product.variationTypes && product.variationTypes.length > 0 && (
              <div className="product-variations">
                <h3 className="variations-title">Product Variations</h3>
                <div className="variations-container">
                  {product.variationTypes.map((type, typeIndex) => (
                    <div key={typeIndex} className="variation-type">
                      <label className="variation-type-label">{type.name}:</label>
                      <div className="variation-options">
                        {type.options.map((option, optionIndex) => (
                          <button
                            key={optionIndex}
                            className={`variation-option ${
                              selectedVariations[type.name] === option ? 'selected' : ''
                            }`}
                            onClick={() => {
                              const newSelectedVariations = {
                                ...selectedVariations,
                                [type.name]: option
                              };
                              setSelectedVariations(newSelectedVariations);
                              
                              // Find matching variation
                              if (product.variations) {
                                const matchingVariation = product.variations.find(variation => {
                                  const variationCombination = variation.combination.split(' - ');
                                  return variationCombination.every((val, index) => 
                                    newSelectedVariations[product.variationTypes[index].name] === val
                                  );
                                });
                                setSelectedVariation(matchingVariation || null);
                              }
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
            </div>

                {/* Selected Variation Details */}
                {selectedVariation && (
                  <div className="selected-variation-details">
                    <div className="variation-info">
                      <span className="variation-combination">{selectedVariation.combination}</span>
                      {selectedVariation.image_url && (
                        <div className="variation-image">
                          <img src={selectedVariation.image_url} alt={selectedVariation.combination} />
                        </div>
                      )}
                    </div>
                    <div className="variation-pricing">
                                              <span className="variation-price">{getCurrencySymbol()}{parseFloat(selectedVariation.price || getPriceForCurrency(product, 'current')).toFixed(2)}</span>
                      <span className="variation-stock">
                        {selectedVariation.stock > 0 ? (
                          <span className="in-stock">In Stock ({selectedVariation.stock})</span>
                        ) : (
                          <span className="out-of-stock">Out of Stock</span>
                        )}
                      </span>
                    </div>
            </div>
                )}
                
                {/* Variation Selection Required Message */}
                {product.variationTypes.length > 0 && !selectedVariation && (
                  <div className="variation-selection-required">
                    <span className="selection-message">Please select all options to continue</span>
                  </div>
                )}
              </div>
            )}

            {/* Product Actions */}
            <div className="product-actions">
              <div className="quantity-selector">
                <label htmlFor="quantity">Quantity:</label>
                <select 
                  id="quantity" 
                  value={quantity} 
                  onChange={(e) => setQuantity(parseInt(e.target.value))}
                  disabled={
                    selectedVariation 
                      ? selectedVariation.stock === 0 
                      : product.stock === 0
                  }
                >
                  {Array.from({ 
                    length: Math.min(10, selectedVariation ? selectedVariation.stock || 0 : product.stock || 1) 
                  }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              
              <div className="action-buttons">
                <button 
                  className="add-to-cart-btn"
                  onClick={handleAddToCart}
                  disabled={
                    (selectedVariation ? selectedVariation.stock === 0 : product.stock === 0) || 
                    (product.has_variations && product.variationTypes && product.variationTypes.length > 0 && !selectedVariation)
                  }
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                  Add to Cart
                </button>
              </div>
            </div>

            {/* Product Details Tabs */}
            <div className="product-tabs">
              <div className="tab-buttons">
                <button 
                  className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
                  onClick={() => setActiveTab('description')}
                >
                  Description
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'specifications' ? 'active' : ''}`}
                  onClick={() => setActiveTab('specifications')}
                >
                  Specifications
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
                  onClick={() => setActiveTab('features')}
                >
                  Features
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'policies' ? 'active' : ''}`}
                  onClick={() => setActiveTab('policies')}
                >
                  Policies
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
                  onClick={() => setActiveTab('reviews')}
                >
                  Reviews
                </button>
              </div>

              <div className="tab-content">
                {/* Description Section */}
                <div className={`description-tab ${activeTab === 'description' ? 'active' : ''}`}>
                  {product.long_description ? (
                    <div className="long-description">
                      <h3>Product Description</h3>
                      <p>{product.long_description}</p>
                    </div>
                  ) : product.description ? (
                    <div className="short-description">
                      <h3>Product Description</h3>
                      <p>{product.description}</p>
                    </div>
                  ) : (
                    <div className="no-description">
                      <p>No description available for this product.</p>
                    </div>
                  )}
                </div>

                {/* Specifications Section */}
                <div className={`specifications-tab ${activeTab === 'specifications' ? 'active' : ''}`}>
                  {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="specs-grid">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="spec-item">
                    <span className="spec-label">{key}:</span>
                    <span className="spec-value">{value}</span>
                  </div>
                ))}
                    </div>
                  )}
                  

                </div>

                {/* Features Section */}
                <div className={`features-tab ${activeTab === 'features' ? 'active' : ''}`}>
                  <h3>Key Features</h3>
                  {product.features && product.features.length > 0 ? (
                    <ul className="features-list">
                      {product.features.map((feature, index) => (
                        <li key={index} className="feature-item">
                          <span className="feature-icon">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="no-features">
                      <p>No features listed for this product.</p>
                    </div>
                  )}
                  
                  {product.tags && product.tags.length > 0 && (
                    <div className="product-tags">
                      <h4>Tags</h4>
                      <div className="tags-container">
                        {product.tags.map((tag, index) => (
                          <span key={index} className="tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Policies Section */}
                <div className={`policies-tab ${activeTab === 'policies' ? 'active' : ''}`}>
                  <div className="policies-grid">
                    {product.warranty && (
                      <div className="policy-item">
                        <h4>Warranty</h4>
                        <p>{product.warranty}</p>
                      </div>
                    )}
                    
                    {product.shipping_info && (
                      <div className="policy-item">
                        <h4>Shipping Information</h4>
                        <p>{product.shipping_info}</p>
                      </div>
                    )}
                    
                    {product.return_policy && (
                      <div className="policy-item">
                        <h4>Return Policy</h4>
                        <p>{product.return_policy}</p>
                      </div>
                    )}
                    
                    {(!product.warranty && !product.shipping_info && !product.return_policy) && (
                      <div className="no-policies">
                        <p>No policy information available for this product.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reviews Section */}
                <div className={`reviews-tab ${activeTab === 'reviews' ? 'active' : ''}`}>
                  {reviewsLoading ? (
                    <div className="reviews-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading reviews...</p>
                    </div>
                  ) : reviews.length > 0 ? (
                    <div className="product-reviews">
                      <div className="reviews-header">
                        <h3>Customer Reviews ({reviews.length})</h3>
                        <div className="reviews-summary">
                          <div className="average-rating">
                            <div className="rating-stars">
                              {[1, 2, 3, 4, 5].map((star) => {
                                const avgRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
                                return (
                                  <span 
                                    key={star} 
                                    className={`star ${star <= Math.round(avgRating) ? 'filled' : 'empty'}`}
                                  >
                                    ★
                                  </span>
                                );
                              })}
                            </div>
                            <span className="rating-number">
                              {(reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="reviews-list">
                        {reviews.map((review) => (
                          <div key={review.id} className="review-item">
                            <div className="review-header">
                              <div className="reviewer-info">
                                <span className="reviewer-name">
                                  {review.customer_email ? review.customer_email.split('@')[0] : 'Anonymous'}
                                </span>
                                {review.verified_purchase && (
                                  <span className="verified-badge">✓ Verified Purchase</span>
                                )}
                              </div>
                              <div className="review-rating">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <span 
                                    key={star} 
                                    className={`star ${star <= review.rating ? 'filled' : 'empty'}`}
                                  >
                                    ★
                                  </span>
                                ))}
                              </div>
                            </div>

                            {review.title && (
                              <div className="review-title">
                                <h4>{review.title}</h4>
                              </div>
                            )}

                            <div className="review-content">
                              <p>{review.comment}</p>
                            </div>

                            <div className="review-footer">
                              <span className="review-date">
                                {new Date(review.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </span>
                              {review.helpful_count > 0 && (
                                <span className="helpful-count">
                                  {review.helpful_count} people found this helpful
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="no-reviews">
                      <div className="no-reviews-icon">📝</div>
                      <h3>No reviews yet</h3>
                      <p>Be the first to review this product! Your feedback helps other customers make informed decisions.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail; 